import { Injectable } from '@angular/core';
import { ParamMap } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  MfaFactor, MfaMethod, RemoveMfaGQL, ResendMfaCodeGQL, StartMfaSetupGQL, UpdateOrganizationMfaEnforcementGQL, VerifyMfaLoginGQL, VerifyMfaSetupGQL
} from 'generated/graphql';

/** Result wrapper for MFA service operations to handle errors without triggering global Apollo error handler */
export interface MfaServiceResult<T = void> {
  success: boolean;
  data?: T;
  errors?: string[];
}

/** Values from `mfaMethod` / `maskedPhone` query params (login + limited-token MFA flows). */
export interface MfaVerificationQueryParams {
  mfaMethod: MfaMethod.Totp | MfaMethod.Sms | null;
  maskedPhone: string | null;
}

@Injectable({ providedIn: 'root' })
export class MfaService {
  constructor(
    private startMfaSetupGQL: StartMfaSetupGQL,
    private verifyMfaSetupGQL: VerifyMfaSetupGQL,
    private verifyMfaLoginGQL: VerifyMfaLoginGQL,
    private resendMfaCodeGQL: ResendMfaCodeGQL,
    private removeMfaGQL: RemoveMfaGQL,
    private updateOrganizationMfaEnforcementGQL: UpdateOrganizationMfaEnforcementGQL,
    private sanitizer: DomSanitizer
  ) {}

  /**
   * Reads MFA hints from the URL (e.g. after redirect with MFA_VERIFICATION_REQUIRED).
   * When `mfaMethod` is absent, `mfaMethod` is null; otherwise it resolves to TOTP or SMS.
   */
  getMfaVerificationQueryParams(query: ParamMap): MfaVerificationQueryParams {
    const raw = query.get('mfaMethod');
    const maskedPhone = query.get('maskedPhone');
    if (!raw) {
      return { mfaMethod: null, maskedPhone };
    }
    const upper = raw.toUpperCase();
    const mfaMethod: MfaMethod.Totp | MfaMethod.Sms =
      upper === MfaMethod.Totp ? MfaMethod.Totp : MfaMethod.Sms;
    return { mfaMethod, maskedPhone };
  }

  startTotpSetup(): Observable<MfaServiceResult<MfaFactor>> {
    return this.startMfaSetupGQL
      .mutate(
        { input: { mfaMethod: MfaMethod.Totp } },
        {
          errorPolicy: 'all',
          context: { ignoreErrors: true }
        }
      )
      .pipe(
        map(result => {
          if (result.errors?.length) {
            return {
              success: false,
              errors: result.errors.map(e => e.message)
            };
          }

          const payload = result.data?.startMfaSetup;

          return {
            success: true,
            data: {
              ...payload?.mfaFactor,
              /* eslint-disable-next-line no-restricted-syntax -- QR code SVG is generated
                 server-side by our own MFA enrollment API, not from user-supplied content. */
              qrCodeSvg: this.sanitizer.bypassSecurityTrustHtml(payload?.mfaFactor?.qrCodeSvg || '')
            } as MfaFactor
          };
        })
      );
  }

  startSmsSetup(phoneNumber: string): Observable<MfaServiceResult> {
    return this.startMfaSetupGQL
      .mutate(
        { input: { mfaMethod: MfaMethod.Sms, phoneNumber } },
        {
          errorPolicy: 'all',
          context: { ignoreErrors: true }
        }
      )
      .pipe(
        map(result => {
          if (result.errors?.length) {
            return {
              success: false,
              errors: result.errors.map(e => e.message)
            };
          }

          return { success: true };
        })
      );
  }

  verifySetup(code: string): Observable<MfaServiceResult<any>> {
    return this.verifyMfaSetupGQL
      .mutate(
        { input: { code } },
        {
          errorPolicy: 'all',
          context: { ignoreErrors: true }
        }
      )
      .pipe(
        map(result => {
          if (result.errors?.length) {
            return {
              success: false,
              errors: result.errors.map(e => e.message)
            };
          }

          const payload = result.data?.verifyMfaSetup;

          return {
            success: true,
            data: payload?.mfaFactor
          };
        })
      );
  }

  verifyLogin(code: string): Observable<MfaServiceResult> {
    return this.verifyMfaLoginGQL
      .mutate(
        { input: { code } },
        {
          errorPolicy: 'all',
          context: { ignoreErrors: true }
        }
      )
      .pipe(
        map(result => {
          if (result.errors?.length) {
            return {
              success: false,
              errors: result.errors.map(e => e.message)
            };
          }

          return { success: true };
        })
      );
  }

  resendSmsCode(): Observable<MfaServiceResult> {
    return this.resendMfaCodeGQL
      .mutate(
        { input: {} },
        {
          errorPolicy: 'all',
          context: { ignoreErrors: true }
        }
      )
      .pipe(
        map(result => {
          if (result.errors?.length) {
            return {
              success: false,
              errors: result.errors.map(e => e.message)
            };
          }

          return { success: true };
        })
      );
  }

  removeMfa(userId: string): Observable<MfaServiceResult> {
    return this.removeMfaGQL.mutate({ input: { userId } }, {
      errorPolicy: 'all',
      context: { ignoreErrors: true }
    })
      .pipe(
        map(result => {
          if (result.errors?.length) {
            return { success: false, errors: result.errors.map(e => e.message) };
          }

          return { success: true };
        })
      );
  }

  enforceMfaForOrganization(enforced: boolean): Observable<MfaServiceResult> {
    return this.updateOrganizationMfaEnforcementGQL.mutate({ input: { enforced } }, {
        errorPolicy: 'all',
        context: { ignoreErrors: true }
      })
      .pipe(
        map(result => {
          if (result.errors?.length) {
            return { success: false, errors: result.errors.map(e => e.message) };
          }

          return { success: true };
        })
      );
  }
}
